import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Divider, Select, Space, Spin, Tooltip, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';

import { AppDispatch, CombinedStates } from '../../../core/types';
import { getSchools } from '../../../core/school/schoolApi';
import { schoolTypeOptions } from '../../../core/utils/helpers';

const { Text } = Typography;
const { Option } = Select;
const LARGE_SELECTION_COUNT = 20;
const APPLY_DELAY_MS = 500;

interface SchoolSelectionProps {
    selectedSchools: string[];
    onChange: (schoolIds: string[]) => void;
    placeholder: string;
}

const SchoolSelection: React.FC<SchoolSelectionProps> = ({
    selectedSchools,
    onChange,
    placeholder,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const [sumTypes, setSumTypes] = useState(false);
    const [activeTypes, setActiveTypes] = useState<string[]>([]);
    const [isApplying, setIsApplying] = useState(false);
    const applyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSelectionRef = useRef<string[] | null>(null);
    const {
        schools,
        isFetching,
    } = useSelector((state: CombinedStates) => state.school);

    useEffect(() => {
        if (isEmpty(schools)) {
            dispatch(getSchools({}));
        }
    }, [dispatch, schools]);

    useEffect(() => () => {
        if (applyTimeoutRef.current) {
            clearTimeout(applyTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        const pendingSelection = pendingSelectionRef.current;
        if (!pendingSelection) {
            return;
        }

        const isApplied = pendingSelection.length === selectedSchools.length
            && pendingSelection.every((id, index) => id === selectedSchools[index]);

        if (isApplied) {
            pendingSelectionRef.current = null;
        }
    }, [selectedSchools]);

    const getCurrentSelection = () => pendingSelectionRef.current ?? selectedSchools;

    const scheduleSelection = (schoolIds: string[]) => {
        const currentSelection = getCurrentSelection();
        const shouldDelay = schoolIds.length >= LARGE_SELECTION_COUNT
            || currentSelection.length >= LARGE_SELECTION_COUNT;

        pendingSelectionRef.current = schoolIds;

        if (!shouldDelay) {
            if (applyTimeoutRef.current) {
                clearTimeout(applyTimeoutRef.current);
                applyTimeoutRef.current = null;
            }
            setIsApplying(false);
            onChange(schoolIds);
            return;
        }

        setIsApplying(true);
        if (applyTimeoutRef.current) {
            clearTimeout(applyTimeoutRef.current);
        }

        applyTimeoutRef.current = setTimeout(() => {
            applyTimeoutRef.current = null;
            onChange(pendingSelectionRef.current ?? schoolIds);
            setIsApplying(false);
        }, APPLY_DELAY_MS);
    };

    const getSchoolIdsByType = (schoolType: string) => schools
        .filter(school => school.type === schoolType)
        .map(school => school.id.toString());

    const handleToggleSumMode = () => {
        if (sumTypes) {
            setSumTypes(false);
            setActiveTypes([]);
            return;
        }

        const selectedSet = new Set(getCurrentSelection());
        const fullySelectedTypes = schoolTypeOptions
            .map(option => option.value)
            .filter(type => {
                const ids = getSchoolIdsByType(type);
                return ids.length > 0 && ids.every(id => selectedSet.has(id));
            });

        setActiveTypes(fullySelectedTypes);
        setSumTypes(true);
    };

    const handleSchoolTypeFilter = (schoolType: string) => {
        const schoolsOfType = getSchoolIdsByType(schoolType);

        if (!sumTypes) {
            scheduleSelection(schoolsOfType);
            return;
        }

        if (schoolsOfType.length === 0) {
            return;
        }

        const currentSelection = getCurrentSelection();
        const typeIdSet = new Set(schoolsOfType);

        if (activeTypes.includes(schoolType)) {
            scheduleSelection(currentSelection.filter(id => !typeIdSet.has(id)));
            setActiveTypes(activeTypes.filter(type => type !== schoolType));
            return;
        }

        const selectedSet = new Set(currentSelection);
        const merged = [...currentSelection];
        schoolsOfType.forEach(id => {
            if (!selectedSet.has(id)) {
                merged.push(id);
            }
        });

        scheduleSelection(merged);
        setActiveTypes([...activeTypes, schoolType]);
    };

    const handleSelectionChange = (schoolIds: string[]) => {
        if (applyTimeoutRef.current) {
            clearTimeout(applyTimeoutRef.current);
            applyTimeoutRef.current = null;
        }
        pendingSelectionRef.current = null;
        setIsApplying(false);
        onChange(schoolIds);

        if (!sumTypes) {
            return;
        }

        const selectedSet = new Set(schoolIds);
        setActiveTypes(activeTypes.filter(type => (
            schools.some(school => school.type === type && selectedSet.has(school.id.toString()))
        )));
    };

    return (
        <Card title="Mokyklų pasirinkimas" className="selection-card">
            {isFetching ? (
                <div className="loading-container">
                    <Spin size="large" />
                    <Text>Kraunamos mokyklos...</Text>
                </div>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                            <Text strong>Pasirinkti pagal mokyklos tipą:</Text>
                            <Tooltip title={sumTypes
                                ? 'Paspaudus tipą, jo mokyklos pridedamos. Paspaudus dar kartą, pašalinamos tik to tipo mokyklos.'
                                : 'Paspaudus tipą, dabartinis pasirinkimas pakeičiamas tik to tipo mokyklomis.'}>
                                <Button
                                    size="small"
                                    type={sumTypes ? 'primary' : 'default'}
                                    onClick={handleToggleSumMode}>
                                    {sumTypes ? 'Sumuoti tipus' : 'Keisti pasirinkimą'}
                                </Button>
                            </Tooltip>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <Space wrap>
                                {schoolTypeOptions.map(schoolType => {
                                    const isTypeActive = sumTypes && activeTypes.includes(schoolType.value);

                                    return (
                                        <Button
                                            key={schoolType.value}
                                            size="small"
                                            icon={isTypeActive ? <CheckOutlined /> : undefined}
                                            onClick={() => handleSchoolTypeFilter(schoolType.value)}
                                            style={{
                                                backgroundColor: schoolType.color,
                                                borderColor: isTypeActive ? '#262626' : schoolType.color,
                                                color: 'white',
                                                boxShadow: isTypeActive ? '0 0 0 2px #fff, 0 0 0 4px #262626' : undefined,
                                            }}>
                                            {schoolType.label}
                                        </Button>
                                    );
                                })}
                            </Space>
                        </div>
                    </div>

                    <Divider />

                    <div>
                        <Text strong>Pasirinktos mokyklos:</Text>
                        <div style={{ position: 'relative', marginTop: 8 }}>
                            <Select
                                mode="multiple"
                                placeholder={placeholder}
                                value={selectedSchools}
                                onChange={handleSelectionChange}
                                style={{ width: '100%' }}
                                allowClear
                                showSearch
                                loading={isApplying}
                                disabled={isApplying}
                                filterOption={(input, option) => {
                                    const optionText = String(option?.children || '').toLowerCase();
                                    return optionText.includes(input.toLowerCase());
                                }}>
                                {schools.map(school => (
                                    <Option key={school.id} value={school.id.toString()}>
                                        {school.name}
                                    </Option>
                                ))}
                            </Select>
                            {isApplying && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 2,
                                    }}>
                                    <Spin size="small" />
                                    <Text>Renkamos mokyklos...</Text>
                                </div>
                            )}
                        </div>
                    </div>
                </Space>
            )}
        </Card>
    );
};

export default SchoolSelection;
